// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * ARCHAT — Cliente HTTP centralizado.
 * Única fuente de requests: fetch y XHR pasan por aquí.
 * Siempre inyecta Authorization Bearer y credentials: 'include'.
 * En 401: lanza error; NUNCA borra sesión (solo logout manual).
 *
 * @file frontend/src/services/apiClient.js
 */

import { STORAGE_KEYS } from '../utils/constants';
import { breadcrumbApi } from './errorBreadcrumbs';
import { devLog } from '../utils/devConsoleLog';

/**
 * Rutas que no deben exigir Bearer en el guard de apiService (login, registro, OAuth, validación, etc.).
 * Coincidencia por substring sobre la URL final (incluye querystring).
 */
export const PUBLIC_ENDPOINTS = [
  '/api/auth/login.php',
  '/api/auth/registrar.php',
  '/api/auth/google_login.php',
  '/api/auth_validate.php',
  '/api/auth/validate.php',
];

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isPublicTokenGuardUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  const n = url.toLowerCase();
  if (PUBLIC_ENDPOINTS.some((ep) => n.includes(ep.toLowerCase()))) {
    return true;
  }
  return (
    n.includes('/auth/login') ||
    n.includes('/auth/registrar') ||
    n.includes('/auth/google_login') ||
    n.includes('auth_validate.php') ||
    n.includes('/auth/check-email') ||
    n.includes('/auth/check-username') ||
    n.includes('/auth/check.php') ||
    n.includes('/auth/generar_captcha') ||
    n.includes('/auth/solicitar_reset') ||
    n.includes('/auth/confirmar_reset') ||
    n.includes('/auth/resend') ||
    n.includes('/auth/confirmar_email') ||
    n.includes('/auth/verificar_email') ||
    n.includes('/auth/activate_account') ||
    n.includes('/auth/complete_activation') ||
    n.includes('/auth/accept_terms') ||
    n.includes('/auth/logout') ||
    n.includes('/legal/') ||
    n.includes('/feature_flags') ||
    n.includes('/system/report_error_auto') ||
    n.includes('/api/log.php')
  );
}

/**
 * Lee el token de autenticación desde localStorage.
 * @returns {string|null}
 */
export function getToken() {
  if (typeof window === 'undefined') return null;
  const primaryToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
  if (primaryToken && String(primaryToken).trim() !== '') {
    // Limpieza legacy al detectar token principal válido.
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    return primaryToken;
  }
  // Compatibilidad temporal: migrar auth_token legacy a archat_token.
  const legacyToken = localStorage.getItem('auth_token');
  if (legacyToken && String(legacyToken).trim() !== '') {
    localStorage.setItem(STORAGE_KEYS.TOKEN, legacyToken);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    return legacyToken;
  }
  return null;
}

/**
 * Cabeceras de auth para requests. Siempre usar con request/upload.
 * @returns {{ Authorization?: string }}
 */
export function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Cabecera X-Tenant-ID si está persistida (alineado a api.js / TenantGuard).
 * @returns {{ 'X-Tenant-ID'?: string }}
 */
function tenantHeadersFromStorage() {
  try {
    if (typeof localStorage === 'undefined') return {};
    const tid = localStorage.getItem('archat_tenant_id');
    if (tid == null || String(tid).trim() === '') return {};
    return { 'X-Tenant-ID': String(tid).trim() };
  } catch (_) {
    return {};
  }
}

/**
 * Realiza una petición HTTP. Inyecta Bearer y credentials: 'include'.
 * X-Requested-With: XMLHttpRequest evita que la petición se trate como navegación (document).
 * En 401 lanza Error con status 401; NO ejecuta clearSession.
 * @param {string} url - URL completa (o ruta relativa; se recomienda absoluta para API)
 * @param {RequestInit} options - Opciones de fetch
 * @returns {Promise<Response>}
 */
export function request(url, options = {}) {
  try {
    breadcrumbApi(typeof url === 'string' ? url : '');
  } catch (_) {}
  const authH = authHeaders();
  const headers = {
    'X-Requested-With': 'XMLHttpRequest',
    ...tenantHeadersFromStorage(),
    ...(options.headers && typeof options.headers === 'object' ? options.headers : {}),
    ...authH
  };
  const finalOptions = {
    ...options,
    credentials: 'include',
    headers
  };
  const token = getToken();
  const urlStr = typeof url === 'string' ? url : '';
  const isPublic = isPublicTokenGuardUrl(urlStr);
  const pathname = (typeof window !== 'undefined' && window.location) ? window.location.pathname : '';
  const source = options?.__source || 'apiClient.request';
  devLog('[API_REQUEST]', { url: urlStr, hasToken: !!token, isPublic, source, wrapper: 'apiClient.js', pathname });
  devLog('[AUTH_TRACE]', { source, endpoint: urlStr, hasToken: !!token, isPublic, pathname, wrapper: 'apiClient.js' });
  return fetch(url, finalOptions);
}

/**
 * Request JSON unificado para llamadas de API.
 * @param {string} url
 * @param {RequestInit} options
 * @returns {Promise<any>}
 */
export async function apiRequest(url, options = {}) {
  const token = getToken();
  const urlStr = typeof url === 'string' ? url : '';
  const isPublic = isPublicTokenGuardUrl(urlStr);
  const pathname = (typeof window !== 'undefined' && window.location) ? window.location.pathname : '';
  const source = options?.__source || 'apiClient.apiRequest';
  devLog('[API_REQUEST]', { url: urlStr, hasToken: !!token, isPublic, source, wrapper: 'apiClient.js', pathname });
  devLog('[AUTH_TRACE]', { source, endpoint: urlStr, hasToken: !!token, isPublic, pathname, wrapper: 'apiClient.js' });
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers && typeof options.headers === 'object' ? options.headers : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const data = await response.json();
  return data;
}

/**
 * Upload por XHR con Authorization y withCredentials.
 * @param {string} url - URL completa
 * @param {FormData} formData
 * @param {(percent: number) => void} [onProgress]
 * @returns {Promise<{ success: boolean, data?: object, [key: string]: any }>}
 */
export function upload(url, formData, onProgress) {
  const token = getToken();
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.withCredentials = true;

    if (onProgress && typeof onProgress === 'function') {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress((e.loaded / e.total) * 100);
        }
      });
    }

    xhr.addEventListener('load', () => {
      const status = xhr.status;
      const raw = xhr.responseText ?? '';
      if (status === 413) {
        let data = {};
        try {
          data = raw.trim() ? JSON.parse(raw) : {};
        } catch (_) {
          /* HTML u otro cuerpo; igual tratamos como 413 */
        }
        reject({
          status: 413,
          error_key: data?.error_key ?? data?.error ?? 'avatar.errors.too_large',
        });
        return;
      }
      let data = {};
      try {
        data = raw.trim() ? JSON.parse(raw) : {};
      } catch (_) {
        const err = new Error('invalid_json_response');
        err.status = status;
        err.parseError = true;
        err.bodySnippet = raw.slice(0, 200);
        err.data = { preview: raw.slice(0, 200) };
        reject(err);
        return;
      }
      if (status >= 200 && status < 300) {
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          resolve(data);
        } else {
          const err = new Error('invalid_response_shape');
          err.status = status;
          err.data = data;
          reject(err);
        }
      } else {
        const err = new Error(data.error || data.message || `Error ${status}`);
        err.status = status;
        err.data = data;
        err.response = { status, data };
        err.code = data.code;
        err.reason = data.reason;
        reject(err);
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error')));
    xhr.addEventListener('timeout', () => reject(new Error('Timeout')));
    xhr.timeout = 30000;

    xhr.open('POST', url);
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.send(formData);
  });
}
